import { useState } from "react";
import { ArrowLeft, Copy, Share2, QrCode, Mail, MessageSquare, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import { Textarea } from "@/ui/components/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/primitives/select";

interface CreatePayinPageProps {
  onBack: () => void;
}

export function CreatePayinPage({ onBack }: CreatePayinPageProps) {
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [qr, setQr] = useState<string | null>(null);

  const [amount, setAmount] = useState<string>("");
  const [currency, setCurrency] = useState<string>("INR");
  const [customerName, setCustomerName] = useState<string>("");
  const [reference, setReference] = useState<string>("");

  const handleGenerate = async () => {
    setError("");
    setLoading(true);
    try {
      const data = await (await import("@/services/api/services/payin")).createManualPayin({
        amount: parseFloat(amount || "0"),
        currency,
        description: "Payment Collection",
        customerName,
        reference,
      });
      const checkoutUrl = (data.checkoutUrl || "").toString().trim();
      setGeneratedLink(checkoutUrl);
      try {
        const { generateQrDataUrl } = await import("@/modules/shared/services/qr");
        const qrUrl = await generateQrDataUrl(checkoutUrl);
        setQr(qrUrl);
      } catch {}
      toast.success("Payment link generated successfully", {
        description: "Share this link with your customer to collect payment",
      });
    } catch (e: any) {
      setError("Failed to create payment link");
      toast.error("Failed to create payment link");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link copied to clipboard");
  };

  const generateReference = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    const refId = `ORD-${timestamp}-${randomNum}`;
    setReference(refId);
    toast.success("Reference ID generated");
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden min-w-0">
      {/* Header */}
      <div>
        <Button variant="ghost" className="mb-4 -ml-3" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Button>
        <h1>Create Payment Collection Link</h1>
        <p className="text-muted-foreground mt-1">
          Generate a payment link to collect money from customers
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card className="lg:col-span-2 p-6">
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                  <Input id="amount" type="number" placeholder="5000" className="pl-7" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency (INR only)</Label>
                <Select defaultValue="inr" onValueChange={(v) => setCurrency(v.toUpperCase())}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inr">INR - Indian Rupee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="customer-name">Customer Name</Label>
                <Input id="customer-name" placeholder="John Doe" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reference">Reference / Order ID</Label>
                <div className="flex gap-2">
                  <Input id="reference" placeholder="ORD-2025-001" value={reference} onChange={(e) => setReference(e.target.value)} className="flex-1" />
                  <Button type="button" variant="outline" onClick={generateReference} className="shrink-0">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate
                  </Button>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button onClick={handleGenerate} className="w-full" disabled={loading}>
                {loading ? "Creating..." : "Create Collection Link"}
              </Button>
            </div>
          </div>
        </Card>

        {/* Preview / Generated Link */}
        {generatedLink && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="mb-4">Link Preview</h3>
              <div className="space-y-4">
                <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                  <div className="text-center p-6">
                    {qr ? (
                      <img alt="Payment QR" src={qr} className="mx-auto h-32 w-32" />
                    ) : (
                      <QrCode className="h-32 w-32 mx-auto text-muted-foreground" />
                    )}
                    <p className="text-xs text-muted-foreground mt-2">QR Code for payment</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Payment Link</p>
                    <p className="text-sm font-mono break-all">{generatedLink}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={handleCopy}
                    >
                      {copied ? (
                        <Check className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      ) : (
                        <Copy className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      )}
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => window.open(generatedLink, "_blank")}>Visit Link</Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Share2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Share
                    </Button>
                  </div>

                  <div className="pt-3 border-t space-y-2">
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Send via Email
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start">
                      <MessageSquare className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Send via SMS
                    </Button>
                  </div>
                </div>
              </div>
              {error && (
                <div className="pt-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
